import { VectorLayer } from '../layers/RendererLayer.js'
import { Style } from 'ol/style.js'
import { Circle, Fill, Stroke, Icon } from 'ol/style.js'
import { Point } from 'ol/geom.js'
import { createElementStyles, getGeometryCache } from './ElementStyles.js'
import { TextureProvider } from '@Renderer/Texture.js'
import { getElement } from '@Utils/Elements.js'
import { createEditStyle } from './../frames/FrameLayer.js'
import * as Utils from '@Utils/Math.js'
import { getIconUrl, renderIcons } from '@Utils/Icons.js'
import { createIconGrid } from '@Utils/IconUtils.js'

let lineSelectedStyle = new Style({
	geometry: function (feature) {
		return getGeometryCache(feature, 'MultiPoint')
	},
	image: new Circle({
		radius: 3,
		fill: new Fill({
			color: "#ffffff"
		})
	})
})
let overLappingStyle = new Style({
	fill: new Fill({
		color: 'rgba(255,0,0,0.5)'
	})
})
let intersectingStyle = new Style({
	fill: new Fill({
		color: 'rgba(255,128,0,0.5)'
	})
})
let renderIconCache = {}
function cacheAllRenderIcons() {
	for (let k in renderIcons) {
		let img = new Image()
		img.src = getIconUrl(k)
		renderIconCache[k] = img
	}
}
cacheAllRenderIcons()
let treeCache = {}
let lineCache = {}
let gridCache = {}
let devicePixelRatio = window.devicePixelRatio || 1
export class ElementsLayer extends VectorLayer {
	map
	useTextures = true;
	scalingContext = null;
	constructor(editor, source) {
		super({
			source: source,
		})
		this.map = editor.map
		this.editor = editor
		let res = this.map.getView().getResolution()
		let _this = this
		this.elementStyles = createElementStyles(false, res)
		this.elementStylesSelected = createElementStyles(true, res)
		this.setStyle(function (feature, res) {
			return _this.getStyle(feature, res)
		})
	}
	clearCache() {
		treeCache = {}
		lineCache = {}
		gridCache = {}
	}
	getLineStringStyle(element, feature, resolution, styleName) {
		let selected = feature.get('selected')
		let opacity = '80'
		let size = (feature.get('lineWidth') ?? element.defaultValues.lineWidth) / resolution
		let id = `${size}-${element.name}`
		if (feature.get('clearCache')) {
			delete lineCache[id]
			delete gridCache[feature.get('uid')]
			feature.unset('clearCache')
		}
		if (!lineCache[id]) {
			lineCache[id] = [new Style({
				stroke: new Stroke({
					color: element.color + opacity,
					lineCap: 'butt',
					width: size
				}),
				geometry: function (f) {
					return getGeometryCache(f, 'LineString')
				}
			})]
		}

		if (selected) {
			return [lineSelectedStyle, new Style({
				stroke: new Stroke({
					color: 'rgba(230, 202, 17, 0.7)',
					lineCap: 'butt',
					width: size
				}),
				geometry: function (f) {
					return getGeometryCache(f, 'LineString')
				}
			})]
		} else {
			return lineCache[id]
		}
	}
	getTreeStyle(element, feature, resolution) {
		let selected = feature.get('selected')
		let opacity = '80'
		let defaultSize = element.name === 'bush' ? TextureProvider.defaultSize.bush : TextureProvider.defaultSize.tree
		let texSize = Math.round(defaultSize / resolution)
		let size = parseInt((texSize * (feature.get('textureSize') ?? 100)) / 100, 10)
		let id = size
		if (!treeCache[id]) {
			treeCache[id] =
				new Style({
					image: new Circle({
						radius: size / 2,
						fill: new Fill({
							color: element.color + opacity
						}),
						stroke: new Stroke({
							color: 'rgba(0,0,0,0.2)',
							width: 2
						})
					})
				})
		}
		if (selected) {
			return [new Style({
				image: new Circle({
					radius: size / 2,
					fill: new Fill({
						color: element.color + opacity
					}),
					stroke: new Stroke({
						color: 'rgba(255,255,255,0.5)',
						width: 2
					})
				})
			})]
		} else {
			return [treeCache[id]]
		}
	}
	getStyle(feature, resolution) {
		if(devicePixelRatio !== (window.devicePixelRatio || 1)) {
			devicePixelRatio = window.devicePixelRatio || 1
			this.clearCache()
		}
		let _this = this
		let name = feature.get('elementName')
		let el = getElement(name)
		if (!el) {
			return []
		}
		let styleName = name
		if ((name === "holeicon" || name === "icon") && (feature.get('iconName') ?? el.defaultValues.iconName)) {
			styleName = "icon-" + feature.get('iconName') ?? el.defaultValues.iconName
			if (feature.get('directional')) {
				styleName = styleName + '-directional-' + (feature.get('directionalIcon') ?? el.defaultValues.directionalIcon)
			}
		} else if (feature.get('isPenaltyArea') === true || name == "penaltyarea") {
			styleName = name + '-' + (feature.get('penaltyAreaType') ?? el.defaultValues.penaltyAreaType ?? 'red')
			if (feature.get('noPlayZone') === true) {
				styleName = styleName + '-' + 'noplayzone'
			}
		}
		let _frameRotation = 0
		let _scalingContext = null
		let _iconScale = 1
		if (this.editor.focusedFrameFeature?.get('holeId')) {
			_iconScale = this.editor.focusedFrameFeature?.get('iconScale') ?? 1
			// calculate the rotation of frame based on the coordinates 
			let frameCoords = this.editor.focusedFrameFeature.getGeometry().getCoordinates()[0]
			let first = frameCoords[0] 
			let second = frameCoords[1]
			let angle = Math.atan2(second[1] - first[1], second[0] - first[0])
			_frameRotation = angle - Utils.degreesToRadians(90)
			_scalingContext = "frame"
		} else if (this.editor.focusedFrameFeature?.get('courseId')) {
			_iconScale = 3 * (this.editor.focusedFrameFeature?.get('iconScale') ?? 1)
			let frameCoords = this.editor.focusedFrameFeature.getGeometry().getCoordinates()[0]
			let first = frameCoords[0] 
			let second = frameCoords[1]
			let angle = Math.atan2(second[1] - first[1], second[0] - first[0])
			_frameRotation = angle - Utils.degreesToRadians(90)
			_scalingContext = "course"
		}
		let _iconResolution = _scalingContext != null ? resolution : 1
		let order = feature.get('order')
		let hidden = feature.get('hidden')
		let filtered = feature.get('filtered')
		let selected = feature.get('selected')
		if (hidden || filtered) {
			return new Style(null)
		}
		let style = []
		if (el.name === 'text') {
			style = [
				new Style({
					renderer: function (coordinates, state) {
						let r = _this.map.getView().getRotation()
						const [x, y] = coordinates
						const feature = state.feature
						const textColor = feature.get('textColor') ?? el.defaultValues.textColor ?? 'black'
						const backgroundShapeColor = feature.get('backgroundShapeColor') ?? el.defaultValues.backgroundShapeColor ?? 'white'
						const ctx = state.context
						let rotation = 0
						rotation = Utils.degreesToRadians(feature.get('rotation') ?? 0)
						ctx.save()
						ctx.translate(x, y)
						ctx.rotate(rotation - r)

						let scaleWithResolution = feature.get('scaleWithResolution') ?? el.defaultValues.scaleWithResolution ?? false
						let res = scaleWithResolution ? resolution : 1
						let textScale = (feature.get('textScale') ?? el.defaultValues.textScale ?? 1)
						textScale = 1 / res * textScale * window.devicePixelRatio
						ctx.scale(textScale, textScale)

						ctx.textAlign = 'center'
						ctx.textBaseline = 'middle'
						ctx.font = '20pt/1 "Tietoevry sans 1",sans-serif'
						let shape = feature.get('backgroundShape') ?? el.defaultValues.backgroundShape ?? 'roundedRectangle'
						const metrics = ctx.measureText(feature.get('text') ?? "Empty")
						let textWidth = metrics.width + 24
						let textHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent + 6
						ctx.fillStyle = backgroundShapeColor
						ctx.beginPath()
						if (shape === 'roundedRectangle') {
							ctx.roundRect(-textWidth / 2, -textHeight / 2 - 3, textWidth, textHeight, 8)
						} else if (shape == 'rectangle') {
							ctx.rect(-textWidth / 2, -textHeight / 2 - 3, textWidth, textHeight)
						} else if (shape == 'circle') {
							let m = Math.max(textWidth, textHeight) * 0.9
							ctx.arc(0, -3, m / 2, 0, Math.PI * 2)
						}
						if (selected) {
							ctx.strokeStyle = 'blue'
							ctx.lineWidth = 2 / textScale
							ctx.shadowBlur = 10
							ctx.shadowColor = 'blue'
							ctx.stroke()
						}
						ctx.fill()
						ctx.fillStyle = textColor
						ctx.fillText(feature.get('text') ?? "Empty", 0, 0)

						ctx.restore()
					},
					/*text: new Text({
						zIndex: 9999,
						font: '30px/1 "Tietoevry sans 1",sans-serif',
						text: feature.get('text') || "Empty text",
						justify: 'center',
						textBaseline: 'middle',
						textAlign: 'center',
						padding: [8/resolution, 6/resolution, 2/resolution, 6/resolution],
						fill: new Fill({
							color: '#000',
						}),
						backgroundFill: new Fill({
							color: '#fff',
						}),
						backgroundStroke: new Stroke({ color: '#fff', width: 16 / resolution, lineCap: 'round', lineJoin: 'round', }),
					}),*/
				}),

			]
		} else if (el.tooltype === 'LineString' && el.name !== "penaltyarea") {
			style = this.getLineStringStyle(el, feature, resolution)
			if (feature.get('isPenaltyArea') === true && !selected) {
				style = [...style, ..._this.elementStyles[styleName]]
			}
		} else if (el.editStyle == 'point') {
			style = [
				new Style({
					image: new Circle({
						radius: 5 / resolution,
						fill: new Fill({
							color: el.color + (!selected ? '80' : '')
						})
					})
				})
			]
		} else if (el.texture == 'tree' || el.texture == 'bush') {
			style = this.getTreeStyle(el, feature, resolution)
		} else if (selected) {
			if (el.tooltype === 'Rectangle') {
				style = createEditStyle(feature)
			} else {
				style = _this.elementStylesSelected[styleName]
			}
		} else {
			style = _this.elementStyles[styleName]
		}
		if (feature.get('overlapping')) {
			style = style.concat(overLappingStyle)
		} else if (feature.get('intersecting')) {
			style = style.concat(intersectingStyle)
		}
		if (!style) {
			console.warn('Style not found for element', styleName, feature)
			return [new Style({
				image: new Circle({
					radius: 20,
					fill: new Fill({
						color: "#ff0000"
					})
				})
			})]
		} else if (el.name === 'iconcollection') { 
			// up the scale only means better quality
			let _scale = window.devicePixelRatio * 10
			if(!gridCache[feature.get('uid')]) {
				gridCache[feature.get('uid')] = createIconGrid(feature, el, _scale, renderIconCache)
			}
			const grid = gridCache[feature.get('uid')]
			const iconCollectionPlacement = feature.get('iconCollectionPlacement') ?? el.defaultValues.iconCollectionPlacement ?? "center"
			let anchor = []
			if (iconCollectionPlacement === "topleft") {
				anchor = [0, 0]
			} else if (iconCollectionPlacement === "topcenter") {
				anchor = [0.5, 0]
			} else if (iconCollectionPlacement === "topright") {
				anchor = [1, 0]
			} else if (iconCollectionPlacement === "middleleft") {
				anchor = [0, 0.5]
			} else if (iconCollectionPlacement === "middleright") {
				anchor = [1, 0.5]
			} else if (iconCollectionPlacement === "bottomleft") {
				anchor = [0, 1]
			} else if (iconCollectionPlacement === "bottomcenter") {
				anchor = [0.5, 1]
			} else if (iconCollectionPlacement === "bottomright") {
				anchor = [1, 1]
			} else if (iconCollectionPlacement === "center") {
				anchor = [0.5, 0.5]
			}
			style.push(
				new Style({
					geometry: function (feature) {
						return new Point(feature.getGeometry().getCoordinates()[1])
					},
					image: new Icon({
						anchor: anchor,
						width: grid.width * _iconScale * 0.8 / resolution / _scale,
						height: grid.height * _iconScale * 0.8 / resolution / _scale,
						anchorXUnits: 'fraction',
						anchorYUnits: 'fraction',
						rotateWithView: true,
						img: grid,
						opacity: selected ? 1 : 0.5
					})
				})
			)
		}
		if (order) {
			let r = this.map.getView().getRotation()
			for (let s of style) {
				s.setZIndex(order)
				let i = s.getImage()
				if (i) {
					if(el.editStyle === 'iconcollection') {
						i.setRotation(- _frameRotation)
					} else if (el.editStyle === 'icon') {
						let rotation = 0
						rotation = Utils.degreesToRadians(feature.get('rotation') ?? 0)
						if (s.rotateWithView) {
							i.setRotation(-r)
						} else {
							i.setRotation(rotation)
						}
					}
					if (el.renderStyle == 'icon') {
						i.setScale(_iconScale / _iconResolution)
					}
					if (el.renderStyle == 'tee') {
						let scaleWithResolution = false
						let res = scaleWithResolution ? resolution : 1
						let scale = 1
						i.setScale(1 / res * scale)
					}
				} else if (el.editStyle === 'text' || el.renderStyle === 'text') {
					/*let rotation = 0
					rotation = Utils.degreesToRadians(feature.get('rotation') ?? 0)
					s.getText()?.setRotation(rotation - r)

					let scaleWithResolution = feature.get('scaleWithResolution') ?? el.defaultValues.scaleWithResolution ?? false;
					let res = scaleWithResolution ? resolution : 1
					let scale = (feature.get('scale') ?? el.defaultValues.scale ?? 1)
					s.getText()?.setScale(1 / res * scale)*/
				}
			}
		}
		return style
	}
}